import Modal from '@/components/ui/modal';
import Appearence from "@/featured/apps/components/settings/SettingsContent";
import AccountTab from "@/featured/apps/components/settings/AccountTab";
import CredentialsTab from "@/featured/apps/components/settings/CredentialsTab";
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useAuthStore from '@/store/auth';
import { User, Sliders, ShieldCheck, Settings, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import clsx from 'clsx';

export type SettingsDesktopTab = 'account' | 'appearance' | 'credentials';

interface TabConfig {
  id: SettingsDesktopTab;
  title: string;
  subtitle: string;
  badge?: string | null;
  icon: React.ComponentType<{ className?: string }>;
}

const SETTINGS_TABS: TabConfig[] = [
  {
    id: 'account',
    title: 'Account Profile',
    subtitle: 'Manage personal details & contact info',
    icon: User,
  },
  {
    id: 'appearance',
    title: 'Display & Maps',
    subtitle: 'Theme, map type, engine & overlays',
    badge: 'WebGL / Leaflet',
    icon: Sliders,
  },
  {
    id: 'credentials',
    title: 'Security & Password',
    subtitle: 'Update password & login credentials',
    icon: ShieldCheck,
  },
];

export default function SettingsDesktopMenu() {
  const [modal, showModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<SettingsDesktopTab>('account');
  const router = useRouter();
  const user = useAuthStore((state) => state.user);

  const setModal = (modalStat: boolean) => {
    showModal(modalStat);
    if (!modalStat) {
      const currentBasePath = router.asPath.split('#')[0].split('?')[0] || '/dashboard';
      router.push(currentBasePath, undefined, { shallow: true });
    }
  };

  useEffect(() => {
    if (router.asPath.includes('settings')) {
      showModal(true);
      if (router.asPath.includes('tab=credentials')) {
        setActiveTab('credentials');
      } else if (router.asPath.includes('tab=appearance')) {
        setActiveTab('appearance');
      } else if (router.asPath.includes('tab=account')) {
        setActiveTab('account');
      }
    } else {
      showModal(false);
    }
  }, [router.asPath]);

  const currentTab = SETTINGS_TABS.find((t) => t.id === activeTab) || SETTINGS_TABS[0];

  return (
    <Modal
      modal={modal}
      setModal={setModal}
      className="!max-w-5xl !p-0 overflow-hidden rounded-2xl border border-slate-200/80 dark:border-gray-800 shadow-2xl bg-white dark:bg-gray-900"
    >
      <div
        className="w-full flex flex-row overflow-hidden"
        style={{
          height: 'min(720px, calc(100vh - 100px))',
        }}
      >
        {/* Left Sidebar */}
        <aside className="w-72 shrink-0 bg-slate-50/90 dark:bg-gray-900/90 border-r border-slate-100 dark:border-gray-800 flex flex-col justify-between select-none">
          {/* Top Section */}
          <div className="flex flex-col">
            {/* Header */}
            <div className="p-5 pb-4 border-b border-slate-100 dark:border-gray-800/80">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow-sm shadow-blue-500/20">
                  <Settings className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#021c32] dark:text-white leading-tight">
                    Settings
                  </h3>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">
                    Preferences & Profile
                  </p>
                </div>
              </div>

              {/* User Profile Card */}
              {user && (
                <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white dark:bg-gray-800/80 border border-slate-200/60 dark:border-gray-700/60 shadow-xs">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                    {user.customer_name ? user.customer_name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                      {user.customer_name || 'User'}
                    </div>
                    <div className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                      {user.email || ''}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Tabs */}
            <nav className="p-3 space-y-1.5" aria-label="Settings Navigation">
              {SETTINGS_TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3.5 py-3 rounded-xl transition-all text-left group cursor-pointer',
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold shadow-xs border border-blue-200/60 dark:border-blue-800/60'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-gray-800/70 hover:text-slate-900 dark:hover:text-slate-200 border border-transparent'
                    )}
                  >
                    <div
                      className={clsx(
                        'p-2 rounded-lg transition-colors shrink-0',
                        isActive
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-slate-200/60 dark:bg-gray-800 text-slate-500 dark:text-slate-400 group-hover:bg-slate-300/60 dark:group-hover:bg-gray-700'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold truncate">{tab.title}</span>
                        {tab.badge && (
                          <span className="text-[8px] font-extrabold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                            {tab.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate mt-0.5 font-normal">
                        {tab.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Bottom Links */}
          <div className="p-4 border-t border-slate-100 dark:border-gray-800/80 space-y-2">
            <div className="flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500 px-1">
              <Link
                href="/terms"
                target="_blank"
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1"
              >
                <span>Terms of Service</span>
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
              <span>•</span>
              <Link
                href="/privacy"
                target="_blank"
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1"
              >
                <span>Privacy Policy</span>
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
            </div>
            <div className="text-center text-[10px] text-slate-400/80 dark:text-slate-600 font-mono tracking-wider">
              MISSI TRACK SYSTEM
            </div>
          </div>
        </aside>

        {/* Right Content Area */}
        <main className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-900 overflow-hidden">
          {/* Header */}
          <div className="px-8 py-5 border-b border-slate-100 dark:border-gray-800 flex items-center justify-between shrink-0 bg-white dark:bg-gray-900 pr-16">
            <div>
              <h2 className="text-base font-bold text-[#021c32] dark:text-white">
                {currentTab.title}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {currentTab.subtitle}
              </p>
            </div>
          </div>

          {/* Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8">
            {activeTab === 'account' && <AccountTab />}
            {activeTab === 'appearance' && <Appearence />}
            {activeTab === 'credentials' && <CredentialsTab />}
          </div>
        </main>
      </div>
    </Modal>
  );
}
